import React, { useEffect } from 'react';
import { Card, Typography, Collapse, Row, Col } from 'antd';

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

const FAQ = () => {
  // Add FAQ Schema markup for SEO
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What age children are Lumi Bears designed for?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Lumi Bears are designed for children ages 2-12, with different models optimized for specific age ranges. Our Classic Bears work well for toddlers and young children (2-6), while our advanced models are suitable for older children (7-12) who benefit from more sophisticated emotional support features."
          }
        },
        {
          "@type": "Question",
          "name": "How do comfort bears help with children's emotional development?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Comfort bears provide consistent emotional support that helps children develop self-regulation skills, emotional vocabulary, and coping strategies. They offer a safe way for children to practice expressing feelings, receive comfort during challenging moments, and build confidence in managing emotions independently."
          }
        },
        {
          "@type": "Question",
          "name": "Are Lumi Bears safe for young children?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all Lumi Bears meet or exceed safety standards for children's toys. They are made with non-toxic materials, have securely attached components, and are designed with age-appropriate features. Each bear is rigorously tested for safety and durability."
          }
        },
        {
          "@type": "Question",
          "name": "How do I clean and maintain a Lumi Bear?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Lumi Bears can be spot cleaned with mild soap and water. For deeper cleaning, the removable covers on many models are machine washable (cold water, gentle cycle). Always check the care instructions included with your specific bear model."
          }
        },
        {
          "@type": "Question",
          "name": "What makes Lumi Bears different from regular teddy bears?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Lumi Bears are specifically designed for emotional support with features like optimal weight distribution for calming, soft materials chosen for sensory comfort, and interactive capabilities that help children develop emotional regulation skills. They're purpose-built wellness tools, not just toys."
          }
        },
        {
          "@type": "Question",
          "name": "When will my Lumi arrive?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Lumi is currently available for preorder at $499. Preordering guarantees you a Lumi in summer 2027. You'll receive tracking information once your order ships, and we provide regular updates on production progress."
          }
        },
        {
          "@type": "Question",
          "name": "Can Lumi Bears help with bedtime routines?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, many families find Lumi Bears particularly helpful for bedtime routines. They provide consistent comfort throughout the night, help children feel secure when separated from parents, and can include interactive features that support calming bedtime activities like breathing exercises or quiet conversations."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer returns or exchanges?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer a 30-day return policy for unused items in original condition. Given that these are comfort objects designed for emotional attachment, we recommend carefully reviewing product details before ordering. Contact our customer service team with any questions about fit or suitability."
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('FAQPage')) {
        existingScript.remove();
      }
    };
  }, []);

  const faqData = [
    {
      question: "What age children are Lumi Bears designed for?",
      answer: "Lumi Bears are designed for children ages 2-12, with different models optimized for specific age ranges. Our Classic Bears work well for toddlers and young children (2-6), while our advanced models are suitable for older children (7-12) who benefit from more sophisticated emotional support features."
    },
    {
      question: "How do comfort bears help with children's emotional development?",
      answer: "Comfort bears provide consistent emotional support that helps children develop self-regulation skills, emotional vocabulary, and coping strategies. They offer a safe way for children to practice expressing feelings, receive comfort during challenging moments, and build confidence in managing emotions independently."
    },
    {
      question: "Are Lumi Bears safe for young children?",
      answer: "Yes, all Lumi Bears meet or exceed safety standards for children's toys. They are made with non-toxic materials, have securely attached components, and are designed with age-appropriate features. Each bear is rigorously tested for safety and durability."
    },
    {
      question: "How do I clean and maintain a Lumi Bear?",
      answer: "Most Lumi Bears can be spot cleaned with mild soap and water. For deeper cleaning, the removable covers on many models are machine washable (cold water, gentle cycle). Always check the care instructions included with your specific bear model."
    },
    {
      question: "What makes Lumi Bears different from regular teddy bears?",
      answer: "Lumi Bears are specifically designed for emotional support with features like optimal weight distribution for calming, soft materials chosen for sensory comfort, and interactive capabilities that help children develop emotional regulation skills. They're purpose-built wellness tools, not just toys."
    },
    {
      question: "When will my Lumi arrive?",
      answer: "Lumi is currently available for preorder at $499. Preordering guarantees you a Lumi in summer 2027. You'll receive tracking information once your order ships, and we provide regular updates on production progress."
    },
    {
      question: "Can Lumi Bears help with bedtime routines?",
      answer: "Yes, many families find Lumi Bears particularly helpful for bedtime routines. They provide consistent comfort throughout the night, help children feel secure when separated from parents, and can include interactive features that support calming bedtime activities like breathing exercises or quiet conversations."
    },
    {
      question: "Do you offer returns or exchanges?",
      answer: "We offer a 30-day return policy for unused items in original condition. Given that these are comfort objects designed for emotional attachment, we recommend carefully reviewing product details before ordering. Contact our customer service team with any questions about fit or suitability."
    },
    {
      question: "Will my child become too dependent on their comfort bear?",
      answer: "Healthy attachment to comfort objects is normal and beneficial for child development. Lumi Bears are designed to support independence by teaching self-regulation skills. As children grow, they typically use their bears less frequently but maintain the emotional skills they've developed."
    },
    {
      question: "How do I introduce a comfort bear to my child?",
      answer: "Start by letting your child explore their new bear during calm, positive moments. Allow them to help choose where the bear 'lives' and establish routines around using it. Be patient - some children attach immediately while others need time to build a relationship with their comfort companion."
    },
    {
      question: "Can comfort bears help children who struggle with big emotions?",
      answer: "Yes, comfort bears are particularly helpful for children who experience intense emotions. They provide a consistent, calming presence during overwhelming moments and can help children practice emotional regulation techniques. The physical comfort combined with predictable interaction patterns supports emotional stability."
    },
    {
      question: "What if my child prefers other animals besides bears?",
      answer: "While our current collection focuses on bears and a few other comfort animals, the emotional support principles work regardless of the specific animal. We chose bears because they're universally appealing and culturally associated with comfort, but the most important factor is that your child feels connected to their companion."
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #f5f5dc 0%, #deb887 100%)',
        padding: '60px 0 40px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
          <Title level={1} style={{ color: '#8b4513', marginBottom: '20px' }}>
            Frequently Asked Questions
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#654321', maxWidth: '600px', margin: '0 auto' }}>
            Everything you need to know about Lumi Bears, emotional support for children, and creating calming routines for your family.
          </Paragraph>
        </div>
      </div>

      {/* FAQ Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Introduction */}
        <Card style={{ marginBottom: '40px', border: '1px solid #e8e8e8' }}>
          <Title level={3} style={{ color: '#8b4513', marginBottom: '16px' }}>
            Common Questions About Comfort Bears and Emotional Support
          </Title>
          <Paragraph style={{ fontSize: '16px', lineHeight: '1.6', color: '#666' }}>
            We understand that choosing emotional support tools for your child is an important decision. 
            Below you'll find answers to the most common questions families have about comfort bears, 
            child development, and creating supportive routines at home.
          </Paragraph>
        </Card>

        {/* FAQ Sections */}
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Title level={3} style={{ color: '#8b4513', marginBottom: '24px' }}>
              Product & Safety Information
            </Title>
            <Collapse 
              size="large"
              style={{ marginBottom: '40px' }}
              expandIcon={({ isActive }) => (
                <span style={{ color: '#8b4513', fontSize: '16px' }}>
                  {isActive ? '−' : '+'}
                </span>
              )}
            >
              {faqData.slice(0, 5).map((item, index) => (
                <Panel 
                  header={
                    <span style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>
                      {item.question}
                    </span>
                  } 
                  key={index}
                  style={{ borderBottom: '1px solid #f0f0f0' }}
                >
                  <Paragraph style={{ fontSize: '15px', lineHeight: '1.6', color: '#666', margin: 0 }}>
                    {item.answer}
                  </Paragraph>
                </Panel>
              ))}
            </Collapse>
          </Col>
        </Row>

        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Title level={3} style={{ color: '#8b4513', marginBottom: '24px' }}>
              Child Development & Usage
            </Title>
            <Collapse 
              size="large"
              style={{ marginBottom: '40px' }}
              expandIcon={({ isActive }) => (
                <span style={{ color: '#8b4513', fontSize: '16px' }}>
                  {isActive ? '−' : '+'}
                </span>
              )}
            >
              {faqData.slice(5, 12).map((item, index) => (
                <Panel 
                  header={
                    <span style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>
                      {item.question}
                    </span>
                  } 
                  key={index + 5}
                  style={{ borderBottom: '1px solid #f0f0f0' }}
                >
                  <Paragraph style={{ fontSize: '15px', lineHeight: '1.6', color: '#666', margin: 0 }}>
                    {item.answer}
                  </Paragraph>
                </Panel>
              ))}
            </Collapse>
          </Col>
        </Row>

        {/* Contact Information */}
        <Card 
          style={{ 
            background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)', 
            border: 'none',
            textAlign: 'center' 
          }}
        >
          <Title level={4} style={{ color: '#8b4513', marginBottom: '16px' }}>
            Still Have Questions?
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
            Our team is here to help you find the perfect comfort companion for your child.
          </Paragraph>
          <Paragraph style={{ fontSize: '15px', color: '#888', margin: 0 }}>
            Email us at <strong style={{ color: '#8b4513' }}>support@lumibears.com</strong> or use our contact form.
            <br />
            We typically respond within 24 hours.
          </Paragraph>
        </Card>

      </div>
    </div>
  );
};

export default FAQ;